import { useState, useMemo, useEffect } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { PageHeader } from '../components/PageHeader';
import { computar, fmtBRL, fmtPct, todayISO } from '../lib/cambio-calc';

const STATUS_OPTIONS = [
  { value: 'aberto',     label: 'Em aberto' },
  { value: 'liquidado',  label: 'Liquidado' },
  { value: 'cancelado',  label: 'Cancelado' },
];

const TIPO_OPTIONS = [
  { value: 'ndf',           label: 'NDF (Non-Deliverable Forward)' },
  { value: 'cambio_pronto', label: 'Câmbio pronto' },
  { value: 'trava',         label: 'Trava' },
  { value: 'd0',            label: 'D0' },
  { value: 'd1',            label: 'D1' },
  { value: 'outro',         label: 'Outro' },
];

const EMPRESAS_PRESET = [
  { nome: 'ETUS Media Holding LTDA', cnpj: '27.670.034/0001-40' },
  { nome: 'BHAZ',                    cnpj: '46.309.445/0001-59' },
  { nome: 'ETUS DIGITAL LLC',        cnpj: '' },
];

const blank = () => ({
  empresa_nome: 'ETUS Media Holding LTDA',
  empresa_cnpj: '27.670.034/0001-40',
  cliente_final: '',
  tipo_operacao: 'ndf',
  operador: '',
  observacao: '',
  dt_inicio: todayISO(),
  dt_vencimento: todayISO(),
  princ_usd: '',
  spot: '',
  spot_venda: '',
  cdi: '',
  sofr: '',
  spread_bps: 10,
  despesa: '',
  status: 'aberto',
});

export function CambioForm({ initial, onSubmit, onCancel, onError }) {
  const [form, setForm] = useState(() => {
    if (initial) {
      return {
        ...blank(),
        ...initial,
        // Datas vem como string ISO do banco; mantem
        // Numericos vem como string (postgres numeric) — string esta ok pro input
        cdi:  initial.cdi  != null ? String(initial.cdi)  : '',
        sofr: initial.sofr != null ? String(initial.sofr) : '',
        princ_usd: initial.princ_usd != null ? String(initial.princ_usd) : '',
        spot:      initial.spot      != null ? String(initial.spot)      : '',
        spot_venda: initial.spot_venda != null ? String(initial.spot_venda) : '',
        despesa:   initial.despesa   != null ? String(initial.despesa)   : '',
      };
    }
    return blank();
  });
  const [errors, setErrors] = useState({});

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Sincroniza CNPJ quando muda empresa
  useEffect(() => {
    const preset = EMPRESAS_PRESET.find(e => e.nome === form.empresa_nome);
    if (preset && form.empresa_cnpj !== preset.cnpj) {
      update('empresa_cnpj', preset.cnpj);
    }
  }, [form.empresa_nome]);

  // Computa tudo derivado em tempo real
  const calc = useMemo(() => {
    const numForm = {
      ...form,
      princ_usd: Number(form.princ_usd) || 0,
      spot: Number(form.spot) || 0,
      cdi:  Number(form.cdi)  || 0,
      sofr: Number(form.sofr) || 0,
      despesa: Number(form.despesa) || 0,
    };
    return computar(numForm);
  }, [form]);

  const handleSubmit = () => {
    const e = {};
    if (!form.empresa_nome?.trim()) e.empresa_nome = 'Obrigatório';
    if (!form.dt_inicio) e.dt_inicio = 'Obrigatório';
    if (!form.dt_vencimento) e.dt_vencimento = 'Obrigatório';
    if (form.dt_vencimento <= form.dt_inicio) e.dt_vencimento = 'Vencimento deve ser depois do início';
    if (!(Number(form.princ_usd) > 0)) e.princ_usd = 'Informe o principal USD';
    if (!(Number(form.spot) > 0)) e.spot = 'Informe a cotação spot';
    if (!(Number(form.cdi) >= 0)) e.cdi = 'CDI inválido';
    if (!(Number(form.sofr) >= 0)) e.sofr = 'SOFR inválido';
    setErrors(e);
    if (Object.keys(e).length > 0) {
      onError?.('Preencha os campos obrigatórios.');
      return;
    }
    // Payload sanitizado pro banco
    onSubmit({
      empresa_nome: form.empresa_nome.trim(),
      empresa_cnpj: form.empresa_cnpj || null,
      cliente_final: form.cliente_final?.trim() || null,
      tipo_operacao: form.tipo_operacao || 'ndf',
      operador: form.operador?.trim() || null,
      observacao: form.observacao?.trim() || null,
      dt_inicio: form.dt_inicio,
      dt_vencimento: form.dt_vencimento,
      princ_usd: Number(form.princ_usd),
      spot:      Number(form.spot),
      spot_venda: Number(form.spot_venda) || null,
      cdi:  Number(form.cdi),
      sofr: Number(form.sofr),
      spread_bps: Number(form.spread_bps) || 0,
      despesa:    Number(form.despesa) || 0,
      status: form.status || 'aberto',
    });
  };

  return (
    <div className="fade-in" style={{ maxWidth: 1180, margin: '0 auto' }}>
      <PageHeader
        title={initial?.id ? 'Editar fechamento' : 'Novo fechamento'}
        subtitle={initial?.codigo_unico
          ? `ID ${initial.codigo_unico} · Edite os campos abaixo`
          : 'Preencha os dados — o código é gerado automaticamente ao salvar'}
        actions={
          <>
            <Button variant="secondary" onClick={onCancel} icon={ArrowLeft}>Voltar</Button>
            <Button onClick={handleSubmit} icon={Check}>Salvar</Button>
          </>
        }
      />

      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 18,
      }}>
        <div>

          {/* Operação */}
          <div className="card" style={{ padding: 22, marginBottom: 14 }}>
            <SectionTitle>Operação</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <Field label="Tipo de operação" required>
                <select className="select"
                  value={form.tipo_operacao}
                  onChange={e => update('tipo_operacao', e.target.value)}>
                  {TIPO_OPTIONS.map(o =>
                    <option key={o.value} value={o.value}>{o.label}</option>
                  )}
                </select>
              </Field>
              <Field label="Empresa emissora" required error={errors.empresa_nome}>
                <select className="select"
                  value={form.empresa_nome}
                  onChange={e => update('empresa_nome', e.target.value)}>
                  {EMPRESAS_PRESET.map(emp =>
                    <option key={emp.nome} value={emp.nome}>{emp.nome}</option>
                  )}
                </select>
              </Field>
              <Field label="Cliente / Contraparte" helper="Quem recebe o pagamento">
                <input className="input"
                  value={form.cliente_final || ''}
                  onChange={e => update('cliente_final', e.target.value)}
                  placeholder="Ex: ETUS LLC, Google News" />
              </Field>
              <Field label="Operador">
                <input className="input"
                  value={form.operador || ''}
                  onChange={e => update('operador', e.target.value)}
                  placeholder="Nome do responsável" />
              </Field>
              <Field label="Data início" required error={errors.dt_inicio}>
                <input className="input" type="date"
                  value={form.dt_inicio}
                  onChange={e => update('dt_inicio', e.target.value)} />
              </Field>
              <Field label="Data vencimento" required error={errors.dt_vencimento}>
                <input className="input" type="date"
                  value={form.dt_vencimento}
                  onChange={e => update('dt_vencimento', e.target.value)} />
              </Field>
              <Field label="Status">
                <select className="select"
                  value={form.status}
                  onChange={e => update('status', e.target.value)}>
                  {STATUS_OPTIONS.map(o =>
                    <option key={o.value} value={o.value}>{o.label}</option>
                  )}
                </select>
              </Field>
              <Field label="Observação" className="col-span-3">
                <input className="input"
                  value={form.observacao || ''}
                  onChange={e => update('observacao', e.target.value)}
                  placeholder="Ex: Combinado com o Marcelo · cotação 14/08 às 14h58" />
              </Field>
            </div>
          </div>

          {/* Câmbio */}
          <div className="card" style={{ padding: 22, marginBottom: 14 }}>
            <SectionTitle>Câmbio & Principal</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <Field label="Principal USD" required error={errors.princ_usd}>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--muted)', fontSize: 13.5, fontWeight: 600,
                  }}>US$</span>
                  <input className="input font-mono"
                    style={{ paddingLeft: 42, fontVariantNumeric: 'tabular-nums' }}
                    type="number" step="0.01"
                    value={form.princ_usd}
                    onChange={e => update('princ_usd', e.target.value)}
                    placeholder="300000.00" />
                </div>
              </Field>
              <Field label="Spot (R$/US$)" required error={errors.spot}
                helper="Cotação à vista (4 casas)">
                <input className="input font-mono"
                  type="number" step="0.0001"
                  value={form.spot}
                  onChange={e => update('spot', e.target.value)}
                  placeholder="5.1890" />
              </Field>
              <Field label="Spot venda" helper="Opcional — só pra registro">
                <input className="input font-mono"
                  type="number" step="0.0001"
                  value={form.spot_venda || ''}
                  onChange={e => update('spot_venda', e.target.value)}
                  placeholder="5.3170" />
              </Field>
            </div>
          </div>

          {/* Taxas */}
          <div className="card" style={{ padding: 22, marginBottom: 14 }}>
            <SectionTitle>Taxas & Banco</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
              <Field label="CDI atual" required error={errors.cdi}
                helper="Decimal · 0.1146 = 11,46% a.a.">
                <input className="input font-mono"
                  type="number" step="0.000001"
                  value={form.cdi}
                  onChange={e => update('cdi', e.target.value)}
                  placeholder="0.1146" />
              </Field>
              <Field label="SOFR/Libor USD" required error={errors.sofr}
                helper="Decimal · 0.0640 = 6,40% a.a.">
                <input className="input font-mono"
                  type="number" step="0.000001"
                  value={form.sofr}
                  onChange={e => update('sofr', e.target.value)}
                  placeholder="0.0640" />
              </Field>
              <Field label="Spread banco (BPS)">
                <input className="input font-mono"
                  type="number" step="1"
                  value={form.spread_bps || 0}
                  onChange={e => update('spread_bps', e.target.value)}
                  placeholder="10" />
              </Field>
              <Field label="Despesa">
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--muted)', fontSize: 13.5, fontWeight: 600,
                  }}>R$</span>
                  <input className="input font-mono"
                    style={{ paddingLeft: 36, fontVariantNumeric: 'tabular-nums' }}
                    type="number" step="0.01"
                    value={form.despesa || ''}
                    onChange={e => update('despesa', e.target.value)}
                    placeholder="0.00" />
                </div>
              </Field>
            </div>
          </div>

          {/* Cálculos derivados — preview ao vivo */}
          <div className="card" style={{ padding: 22 }}>
            <SectionTitle>Cálculos automáticos (preview ao vivo)</SectionTitle>
            <CalcGrid>
              <CalcRow label="Dias úteis (DU)"          value={calc?.du ?? '—'} />
              <CalcRow label="Dias corridos (DC)"       value={calc?.dc ?? '—'} />
              <CalcRow label="Princ. R$"                value={`R$ ${fmtBRL(calc?.princ_brl)}`} />
              <CalcRow label="Juros real BRL no PE"     value={fmtPct(calc?.jrs_brl)} />
              <CalcRow label="Juros USD no PE"          value={fmtPct(calc?.jrs_usd)} />
              <CalcRow label="Taxa implícita"           value={fmtPct(calc?.tx_impl)} />
              <CalcRow label="Desv. implícita (R$)"     value={fmtBRL(calc?.desv_impl, 4)} />
              <CalcRow label="Futuro teórico"           value={fmtBRL(calc?.futuro_teorico, 4)} />
              <CalcRow label="Princ. Bruto"             value={`R$ ${fmtBRL(calc?.princ_bruto)}`} />
              <CalcRow label="Prêmio Bruto"             value={`R$ ${fmtBRL(calc?.premio_bruto)}`} />
              <CalcRow label={`IR (${(calc?.ir_aliq * 100 || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)`} value={`R$ ${fmtBRL(calc?.ir_val)}`} />
              <CalcRow label="Prêmio Líquido"           value={`R$ ${fmtBRL(calc?.premio_liquido)}`} />
            </CalcGrid>
            <div style={{
              fontSize: 11, color: 'var(--muted)', marginTop: 14, lineHeight: 1.5,
            }}>
              <strong>Fórmulas:</strong>{' '}
              <code style={{ background: 'var(--surface-3)', padding: '1px 5px', borderRadius: 3, fontSize: 10.5 }}>DU = NETWORKDAYS(início, venc, feriados_SP) − 1</code>{' '}·{' '}
              <code style={{ background: 'var(--surface-3)', padding: '1px 5px', borderRadius: 3, fontSize: 10.5 }}>Juros BRL = (1+CDI)^(DU/252) − 1</code>{' '}·{' '}
              <code style={{ background: 'var(--surface-3)', padding: '1px 5px', borderRadius: 3, fontSize: 10.5 }}>Implícita = (1+BRL)/(1+USD) − 1</code>{' '}·{' '}
              IR 20% se DC&nbsp;&gt;&nbsp;180, 22.5% caso contrário.
            </div>
          </div>
        </div>

        {/* Sidebar: resultado */}
        <div>
          <div style={{
            position: 'sticky', top: 16,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 14,
            padding: 22,
          }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '.14em',
              textTransform: 'uppercase', color: 'var(--primary)',
              background: 'var(--primary-soft, rgba(59,228,118,0.12))',
              padding: '3px 10px', borderRadius: 99,
              display: 'inline-block', marginBottom: 14,
            }}>Resultado final</span>

            <div style={{ padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 600 }}>Total a Crédito</div>
              <div className="font-display" style={{ fontSize: 28, fontWeight: 600, fontVariantNumeric: 'tabular-nums', margin: '4px 0' }}>
                R$ {fmtBRL(calc?.total_credito)}
              </div>
              <div className="font-mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                Princ R$ + Prêmio Líq + Despesa
              </div>
            </div>

            <div style={{ padding: '14px 0' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 600 }}>Taxa Líquida Final</div>
              <div className="font-display" style={{ fontSize: 34, fontWeight: 600, fontVariantNumeric: 'tabular-nums', margin: '4px 0', color: 'var(--primary)' }}>
                {fmtBRL(calc?.taxa_final, 4)}
              </div>
              <div className="font-mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                R$ por US$ 1,00
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 10.5, fontWeight: 700, letterSpacing: '.14em',
      textTransform: 'uppercase', color: 'var(--muted)',
      display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
    }}>
      <span>{children}</span>
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}

function CalcGrid({ children }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1,
      background: 'var(--border)',
      border: '1px solid var(--border)',
      borderRadius: 10, overflow: 'hidden',
    }}>{children}</div>
  );
}
function CalcRow({ label, value }) {
  return (
    <div style={{
      background: 'var(--surface)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 14px', gap: 12,
    }}>
      <span style={{
        fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase',
        letterSpacing: '.08em', fontWeight: 600,
      }}>{label}</span>
      <span className="font-mono" style={{
        fontSize: 14, fontWeight: 600, color: 'var(--ink)',
        fontVariantNumeric: 'tabular-nums', textAlign: 'right',
      }}>{value}</span>
    </div>
  );
}
